import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trpc } from '../lib/trpc';
import { useAuth } from '../hooks/useAuth';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Avatar } from '../components/ui/avatar';
import { Spinner } from '../components/ui/spinner';
import { ExternalLink, Users, Pencil, Check, X, Share2, QrCode, CheckCircle2, Download } from 'lucide-react';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import { HandshakePath } from '../components/HandshakePath';
import { VideoGuide } from '../components/VideoGuide';
import { useNicknames } from '../hooks/useNicknames';
import { buildSosInviteUrl } from '../lib/inviteUrl';

export function CollectionPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isSos = searchParams.get('sos') === 'true';
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userId = useAuth((s) => s.userId);
  const resolve = useNicknames();
  const utils = trpc.useUtils();

  const { data: me } = trpc.user.me.useQuery();
  const { data: collection, isLoading } = trpc.collection.getById.useQuery({ id: id! }, { enabled: !!id, refetchInterval: 30000 });
  const { data: pathToCreator } = trpc.connection.findPath.useQuery(
    { targetUserId: collection?.creatorId! },
    { enabled: !!collection && collection.creatorId !== userId }
  );
  const createObligation = trpc.obligation.create.useMutation({
    onSuccess: () => utils.collection.getById.invalidate({ id: id! }),
    onError: (e) => { if (e.message === 'MIN_AMOUNT_USD') setError(t('collection.minAmount')); },
  });
  const closeCollection = trpc.collection.close.useMutation({ onSuccess: () => utils.collection.getById.invalidate({ id: id! }) });
  const confirmPayment = trpc.obligation.confirmPayment.useMutation({ onSuccess: () => utils.collection.getById.invalidate({ id: id! }) });
  const updateAmount = trpc.obligation.updateAmount.useMutation({
    onSuccess: () => utils.collection.getById.invalidate({ id: id! }),
    onError: (e) => { if (e.message === 'MIN_AMOUNT_USD') setEditError(t('collection.minAmount')); },
  });
  const updateChatLink = trpc.collection.updateChatLink.useMutation({ onSuccess: () => utils.collection.getById.invalidate({ id: id! }) });
  const updateCollectionAmount = trpc.collection.updateAmount.useMutation({ onSuccess: () => utils.collection.getById.invalidate({ id: id! }) });
  const acceptSos = trpc.invite.acceptSos.useMutation({
    onSuccess: () => utils.collection.getById.invalidate({ id: id! }),
  });

  const [amount, setAmount] = useState('1');
  const [sosCopied, setSosCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const setBudget = trpc.user.setMonthlyBudget.useMutation();
  const [editingChatLink, setEditingChatLink] = useState(false);
  const [chatLinkValue, setChatLinkValue] = useState('');
  const [chatLinkError, setChatLinkError] = useState('');
  const [error, setError] = useState('');

  // Edit collection goal amount state
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalAmount, setGoalAmount] = useState('');
  const [goalError, setGoalError] = useState('');


  // Edit amount state
  const [editingOblId, setEditingOblId] = useState<string | null>(null);
  const [editAmount, setEditAmount] = useState('');
  const [editError, setEditError] = useState('');


  // Auto-connect to creator when opened via SOS invite link
  useEffect(() => {
    if (isSos && collection && userId && collection.creatorId !== userId && !acceptSos.isSuccess && !acceptSos.isPending) {
      acceptSos.mutate({ collectionId: collection.id });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSos, collection?.id, userId]);



  if (isLoading) return <div className="flex justify-center py-12"><Spinner /></div>;
  if (!collection) return <div className="p-4 text-center text-gray-500 dark:text-gray-300">{t('common.notFound')}</div>;

  const isOwner = collection.creatorId === userId;
  const hasObligation = collection.obligations.some((o) => o.userId === userId);
  const hasGoal = collection.amount != null && collection.amount > 0;
  const percentage = hasGoal ? (collection.currentAmount / collection.amount!) * 100 : 0;
  const statusVariant = collection.status === 'ACTIVE' ? 'success' : collection.status === 'BLOCKED' ? 'warning' : collection.status === 'CLOSED' ? 'default' : 'danger';

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ACTIVE': return t('collection.active');
      case 'BLOCKED': return t('collection.blocked');
      case 'CLOSED': return t('collection.closed');
      case 'CANCELLED': return t('collection.cancelled');
      default: return status;
    }
  };

  const handleSubmitObligation = () => {
    const num = Number(amount);
    // Amounts are in $ only: 1 $ = 1 unit of participation, no currency conversion.
    if (!Number.isInteger(num) || num < 1) { setError(t('collection.minAmount')); return; }
    setError('');
    createObligation.mutate(
      { collectionId: collection.id, amount: num },
      {
        onSuccess: () => {
          // If user came via SOS link (bypassed onboarding) — also save as their potential
          if (isSos && (!me?.monthlyBudget || me.monthlyBudget === 0)) {
            setBudget.mutate({ amount: num });
          }
        },
      }
    );
    setAmount('');
  };



  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-3">
        <Avatar src={collection.creator.photoUrl} name={resolve(collection.creatorId, collection.creator.name)} size="lg" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(`/profile/${collection.creatorId}`)} className="text-lg font-bold text-gray-900 dark:text-white hover:underline">{resolve(collection.creatorId, collection.creator.name)}</button>
            <span className="flex items-center gap-1 text-xs text-gray-400">
              <Users className="w-3 h-3" />
              {(collection.creator as any).connectionCount ?? 0}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant={statusVariant}>{getStatusLabel(collection.status)}</Badge>
            <Badge variant="info">{collection.type === 'EMERGENCY' ? t('collection.emergency') : t('collection.regular')}</Badge>
          </div>
        </div>
      </div>

      {pathToCreator?.path && pathToCreator.path.length > 1 && (
        <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <p className="text-xs text-gray-500 dark:text-gray-300 mb-2">{t('collection.connectionToCreator')}</p>
          <HandshakePath path={pathToCreator.path} onUserClick={(uid) => navigate(`/profile/${uid}`)} resolveName={resolve} />
        </div>
      )}

      <Card>
        <CardContent className="py-4">
          <div className="flex justify-between items-end mb-2">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-300">{t('collection.collected')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">${Math.round(collection.currentAmount)} USD</p>
            </div>
            <div className="text-right">
              {isOwner && (collection.status === 'ACTIVE' || collection.status === 'BLOCKED') && editingGoal ? (
                <div className="flex flex-col gap-1 items-end">
                  <div className="flex gap-1 items-center">
                    <div className="w-24">
                      <Input
                        type="number"
                        min={10}
                        step={1}
                        value={goalAmount}
                        onChange={(e) => { setGoalAmount(e.target.value); setGoalError(''); }}
                        error={goalError}
                        onFocus={(e) => { setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300); }}
                      />
                    </div>
                    <span className="h-[38px] flex items-center text-sm font-medium text-gray-500 dark:text-gray-300" aria-hidden="true">$</span>
                    <button
                      onClick={() => {
                        const num = Number(goalAmount);
                        if (!Number.isInteger(num) || num < 10) { setGoalError(t('create.minAmount')); return; }
                        updateCollectionAmount.mutate(
                          { id: collection.id, amount: num },
                          { onSuccess: () => setEditingGoal(false) }
                        );
                      }}
                      disabled={updateCollectionAmount.isPending}
                      className="p-2 text-green-600 hover:text-green-700 disabled:opacity-50"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => { setEditingGoal(false); setGoalError(''); }}
                      className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {hasGoal && (
                    <div className="flex items-start gap-1 justify-end">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-300">{t('collection.goal')}</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">${collection.amount} USD</p>
                      </div>
                      {isOwner && (collection.status === 'ACTIVE' || collection.status === 'BLOCKED') && (
                        <button
                          onClick={() => {
                            setGoalAmount(String(collection.amount ?? ''));
                            setGoalError('');
                            setEditingGoal(true);
                          }}
                          className="mt-5 p-1.5 text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100"
                          title={t('collection.editGoal')}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                  {!hasGoal && isOwner && (collection.status === 'ACTIVE' || collection.status === 'BLOCKED') && (
                    <button
                      onClick={() => { setGoalAmount(''); setGoalError(''); setEditingGoal(true); }}
                      className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      <Pencil className="w-3.5 h-3.5" /> {t('collection.setGoal')}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          {hasGoal && !editingGoal && <><Progress value={collection.currentAmount} max={collection.amount!} /><p className="text-xs text-gray-500 dark:text-gray-300 mt-1 text-right">{percentage.toFixed(0)}%</p></>}
        </CardContent>
      </Card>

      {(hasObligation || isOwner) && (
        <div className="space-y-1">
          {editingChatLink ? (
            <div className="flex gap-2 items-start">
              <div className="flex-1">
                <Input
                  type="url"
                  placeholder="https://t.me/..."
                  value={chatLinkValue}
                  onChange={(e) => { setChatLinkValue(e.target.value); setChatLinkError(''); }}
                  error={chatLinkError}
                  onFocus={(e) => { setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300); }}
                />
              </div>
              <button
                onClick={() => {
                  try { new URL(chatLinkValue); } catch { setChatLinkError(t('create.invalidUrl')); return; }
                  updateChatLink.mutate({ id: collection.id, chatLink: chatLinkValue }, { onSuccess: () => setEditingChatLink(false) });
                }}
                disabled={updateChatLink.isPending}
                className="p-2 text-green-600 hover:text-green-700 disabled:opacity-50"
              >
                <Check className="w-4 h-4" />
              </button>
              <button onClick={() => { setEditingChatLink(false); setChatLinkError(''); }} className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-300">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {collection.chatLink && /^https?:\/\//i.test(collection.chatLink) ? (
                <a href={collection.chatLink} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg text-blue-600 dark:text-blue-400 text-sm hover:underline">
                  <ExternalLink className="w-4 h-4" /> {t('collection.openChat')}
                </a>
              ) : isOwner ? (
                <span className="flex-1 p-3 text-sm text-gray-400 dark:text-gray-500">{t('collection.noChatLink')}</span>
              ) : null}
              {isOwner && (
                <button
                  onClick={() => { setChatLinkValue(collection.chatLink || ''); setChatLinkError(''); setEditingChatLink(true); }}
                  className="p-2.5 text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100"
                  title={t('collection.editChatLink')}
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {!isOwner && !hasObligation && collection.status === 'ACTIVE' && (
        <Card>
          <CardHeader><h3 className="font-semibold text-gray-900 dark:text-white">{t('collection.expressIntention')}</h3></CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input type="number" min={1} step={1} placeholder={t('collection.amountPlaceholder')} hint={t('hints.intentionAmount')} value={amount} onChange={(e) => setAmount(e.target.value)} error={error}
                    onFocus={(e) => { setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300); }}
                  />
                </div>
                <span className="h-[38px] flex items-center text-sm font-medium text-gray-500 dark:text-gray-300" aria-hidden="true">$</span>
                <Button onClick={handleSubmitObligation} disabled={createObligation.isPending}>{t('collection.submit')}</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {isOwner && (collection.status === 'ACTIVE' || collection.status === 'BLOCKED') && (() => {
        const sosUrl = buildSosInviteUrl(collection.id);
        const isRegular = collection.type === 'REGULAR';
        // Print-quality PNG of the same QR, e.g. to put on a website or a poster
        const downloadQr = () => {
          const canvas = document.getElementById(`qr-download-${collection.id}`) as HTMLCanvasElement | null;
          if (!canvas) return;
          const a = document.createElement('a');
          a.href = canvas.toDataURL('image/png');
          a.download = `social-organizer-qr-${collection.id}.png`;
          a.click();
        };
        return (
          <div className="space-y-2">
            <div className="flex gap-2">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(sosUrl);
                  setSosCopied(true);
                  setTimeout(() => setSosCopied(false), 2500);
                }}
                className="flex-1 flex items-center justify-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm font-medium hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
              >
                {sosCopied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
                {isRegular
                  ? (sosCopied ? t('collection.linkCopied') : t('collection.shareLinkButton'))
                  : (sosCopied ? t('collection.sosLinkCopied') : t('collection.sosInviteButton'))}
              </button>
              <button
                onClick={() => setShowQr((v) => !v)}
                className={`p-3 rounded-lg border text-sm font-medium transition-colors ${showQr ? 'bg-red-100 dark:bg-red-900/50 border-red-300 dark:border-red-700 text-red-700 dark:text-red-300' : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40'}`}
                title={t('collection.qrTitle')}
                aria-label={t('collection.qrTitle')}
              >
                <QrCode className="w-4 h-4" />
              </button>
            </div>
            {showQr && (
              <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                <QRCodeSVG value={sosUrl} size={180} level="M" includeMargin />
                <p className="text-xs text-gray-400 text-center">{t('collection.qrScanHint')}</p>
                <QRCodeCanvas id={`qr-download-${collection.id}`} value={sosUrl} size={1024} level="M" includeMargin className="hidden" />
                <button
                  type="button"
                  onClick={downloadQr}
                  className="flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  {t('collection.qrDownload')}
                </button>
              </div>
            )}
            <div className="flex justify-center">
              <VideoGuide />
            </div>
          </div>
        );
      })()}

      {isOwner && (collection.status === 'ACTIVE' || collection.status === 'BLOCKED') && (
        <Button variant="outline" className="w-full" onClick={() => closeCollection.mutate({ id: collection.id })}>{t('collection.close')}</Button>
      )}

      <Card>
        <CardHeader><h3 className="font-semibold text-gray-900 dark:text-white">{t('collection.participants')} ({collection.obligations.length})</h3></CardHeader>
        <CardContent>
          {collection.obligations.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-300 text-center py-2">{t('collection.noParticipants')}</p>
          ) : (
            <div className="space-y-2">
              {collection.obligations.map((obl) => {
                const oblData = obl as typeof obl & { confirmedAt?: string | null };
                const isMyObl = obl.userId === userId;
                const canEdit = isMyObl && (collection.status === 'ACTIVE' || collection.status === 'BLOCKED');
                const isEditing = editingOblId === obl.id;

                const startEdit = () => {
                  setEditingOblId(obl.id);
                  setEditAmount(String(obl.amount));
                  setEditError('');
                };

                const cancelEdit = () => {
                  setEditingOblId(null);
                  setEditError('');
                };

                const saveEdit = () => {
                  const num = Number(editAmount);
                  if (!Number.isInteger(num) || num < 1) { setEditError(t('collection.minAmount')); return; }
                  setEditError('');
                  updateAmount.mutate(
                    { obligationId: obl.id, amount: num },
                    { onSuccess: () => setEditingOblId(null) }
                  );
                };

                return (
                  <div key={obl.id} className="py-1">
                    <div className="flex items-center justify-between">
                      <button onClick={() => navigate(`/profile/${obl.userId}`)} className="flex items-center gap-2 hover:underline">
                        <Avatar src={obl.user.photoUrl} name={resolve(obl.userId, obl.user.name)} size="sm" />
                        <span className="text-sm text-gray-900 dark:text-white">{resolve(obl.userId, obl.user.name)}</span>
                        <span className="flex items-center gap-0.5 text-xs text-gray-400">
                          <Users className="w-3 h-3" />
                          {(obl.user as any).connectionCount ?? 0}
                        </span>
                      </button>
                      {!isEditing && (
                        <div className="flex items-center gap-2">
                          <div className="text-right">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">${Math.round(obl.amount)} USD</span>
                            {(oblData as any).confirmedAt && (
                              <p className="text-xs text-green-600 dark:text-green-400 font-medium">{t('collection.paymentConfirmed')}</p>
                            )}
                          </div>
                          {isOwner && (collection.status === 'ACTIVE' || collection.status === 'BLOCKED') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                confirmPayment.mutate({ obligationId: obl.id });
                              }}
                              disabled={confirmPayment.isPending}
                              className={`p-2.5 -m-1.5 transition-colors disabled:opacity-50 ${
                                (oblData as any).confirmedAt
                                  ? 'text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300'
                                  : 'text-gray-300 dark:text-gray-600 hover:text-green-500 dark:hover:text-green-400'
                              }`}
                              title={(oblData as any).confirmedAt ? t('collection.unconfirmPayment') : t('collection.confirmPayment')}
                            >
                              <CheckCircle2 className="w-5 h-5" />
                            </button>
                          )}
                          {canEdit && (
                            <button onClick={(e) => { e.stopPropagation(); startEdit(); }} className="p-2.5 -m-1.5 text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100 active:text-blue-500" title={t('collection.editAmount')}>
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    {isEditing && (
                      <div className="mt-2 space-y-2 pl-8">
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <Input type="number" min={1} step={1} value={editAmount} onChange={(e) => setEditAmount(e.target.value)} error={editError}
                              onFocus={(e) => { setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300); }}
                            />
                          </div>
                          <span className="h-[38px] flex items-center text-sm font-medium text-gray-500 dark:text-gray-300" aria-hidden="true">$</span>
                          <button onClick={saveEdit} disabled={updateAmount.isPending} className="p-2 text-green-600 hover:text-green-700 disabled:opacity-50" title={t('collection.saveAmount')}>
                            <Check className="w-4 h-4" />
                          </button>
                          <button onClick={cancelEdit} className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-300" title={t('collection.cancelEdit')}>
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
